import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
    format,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
    parse,
} from "date-fns";
import { RouteProp, useFocusEffect, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import {
    ExerciseService,
    ExerciseDto,
} from "../../../services/ExerciseService";
import { useAuth } from "../../../../context/AuthContext";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type ExerciseHistoryRouteProp = RouteProp<AuthStackParamList, "ExerciseHistory">;

export function ExerciseHistoryScreen() {
    const route = useRoute<ExerciseHistoryRouteProp>();
    const { user } = useAuth();
    const today = new Date();
    const initialRange = resolveHistoryRange(route.params, today);
    const effectiveUserId = useMemo(() => {
        return initialRange.devoteeId || user?.userId || "";
    }, [initialRange.devoteeId, user?.userId]);

    const [filterType, setFilterType] =
        useState<FilterType>(initialRange.hasNavigationDates ? "Custom" : "Monthly");

    const [fromDate, setFromDate] = useState(initialRange.fromDate);
    const [toDate, setToDate] = useState(initialRange.toDate);

    const [entries, setEntries] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    /* ================= EDIT STATES ================= */

    const [editVisible, setEditVisible] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [editMinutes, setEditMinutes] = useState("");
    const [editExerciseTypeId, setEditExerciseTypeId] =
        useState("");
    const [editOtherType, setEditOtherType] = useState("");
    const [types, setTypes] = useState<any[]>([]);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const editMinutesError = useMemo(() => getMinutesValidationError(editMinutes), [editMinutes]);

    function tryParseHistoryDate(dateStr?: string | null): Date | null {
        if (!dateStr) return null;

        const a = parse(dateStr, "dd-MM-yyyy", new Date());
        if (!isNaN(a.getTime())) return a;

        const b = parse(dateStr, "yyyy-MM-dd", new Date());
        if (!isNaN(b.getTime())) return b;

        const c = new Date(dateStr);
        if (!isNaN(c.getTime())) return c;

        return null;
    }

    /* ================= LOAD TYPES ================= */

    useEffect(() => {
        loadTypes();
    }, []);

    const loadTypes = async () => {
        const res = await ExerciseService.getExerciseTypes(
            "Exercise"
        );
        if (res?.status === 200) {
            setTypes(res.data || []);
        }
    };

    /* ================= FILTER ================= */

    const handleFilterChange = (type: FilterType) => {
        setFilterType(type);

        let start: Date;
        let end: Date;

        switch (type) {
            case "Weekly":
                start = startOfWeek(today);
                end = endOfWeek(today);
                break;
            case "Quarterly":
                start = startOfQuarter(today);
                end = endOfQuarter(today);
                break;
            case "Yearly":
                start = startOfYear(today);
                end = endOfYear(today);
                break;
            default:
                start = startOfMonth(today);
                end = endOfMonth(today);
        }

        setFromDate(format(start, "yyyy-MM-dd"));
        setToDate(format(end, "yyyy-MM-dd"));
    };

    /* ================= LOAD HISTORY ================= */

    const loadHistory = useCallback(async () => {
        if (!effectiveUserId) return;

        setLoading(true);

        try {
            const res =
                await ExerciseService.getExerciseHistory({
                    userId: effectiveUserId,
                    fromDate,
                    toDate,
                });

            if (res?.status === 200) {
                setEntries(res.data.records || []);
            } else {
                setEntries([]);
            }
        } catch {
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [effectiveUserId, fromDate, toDate]);

    useFocusEffect(
        useCallback(() => {
            loadHistory();
        }, [loadHistory])
    );

    const onRefresh = async () => {
        setRefreshing(true);
        try {
            await loadHistory();
        } finally {
            setRefreshing(false);
        }
    };

    /* ================= DATE PICKER ================= */

    const openPicker = (type: "from" | "to") => {
        DateTimePickerAndroid.open({
            value: new Date(type === "from" ? fromDate : toDate),
            mode: "date",
            maximumDate: new Date(),
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    const formatted = format(
                        selectedDate,
                        "yyyy-MM-dd"
                    );
                    type === "from"
                        ? setFromDate(formatted)
                        : setToDate(formatted);
                    setFilterType("Custom");
                }
            },
        });
    };

    /* ================= DELETE ================= */

    const handleDelete = (id: number) => {
        setPendingDeleteId(id);
    };

    /* ================= EDIT ================= */

    const openEditModal = (item: any) => {
        const otherEnum = types.find(t => t.value === "Other");

        setEditData(item);

        const [hoursPart = "0", minutesPart = "0"] = String(item.data.duration || "00:00:00").split(":");
        const totalMinutes = (parseInt(hoursPart, 10) || 0) * 60 + (parseInt(minutesPart, 10) || 0);
        setEditMinutes(totalMinutes ? String(totalMinutes) : "");

        setEditExerciseTypeId(
            otherEnum?.enumerationId || item.data.exerciseTypeId
        );

        setEditOtherType(item.data.otherType || "");

        setEditVisible(true);
    };

    const formatDuration = (minutesInput: string) => {
        const totalMinutes = parseValidMinutes(minutesInput);
        if (totalMinutes == null) return null;

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours
            .toString()
            .padStart(2, "0")}:${minutes
                .toString()
                .padStart(2, "0")}:00`;
    };

    const handleUpdate = async () => {
        const formattedDuration =
            formatDuration(editMinutes);

        if (editMinutesError) {
            Flash.error(editMinutesError);
            return;
        }

        if (!formattedDuration) {
            Flash.error("Enter valid minutes");
            return;
        }

        const selected = types.find(
            (t) =>
                t.enumerationId === editExerciseTypeId
        );

        const payload: ExerciseDto = {
            exerciseId: editData.data.exerciseId,
            userId: user?.userId,
            date: format(
                tryParseHistoryDate(editData.date) || new Date(),
                "yyyy-MM-dd"
            ),
            exerciseTypeId: editExerciseTypeId,
            otherType:
                selected?.value === "Other"
                    ? editOtherType?.trim() || null
                    : null,
            duration: formattedDuration,
        };

        const res =
            await ExerciseService.update(payload);

        if (res?.status === 200) {
            Flash.success("Success!", "Exercise Updated successfully");
            setEditVisible(false);
            loadHistory();
        } else {
            Flash.error("Update failed");
        }
    };

    /* ================= UI ================= */

    return (
        <DivineBackground overlayOpacity={0.92}>
            <View style={{ flex: 1 }}>
                <Header
                    title="Exercise History"
                    showBack
                    showProfile
                    showHome
                />

                <ScrollView
                    contentContainerStyle={styles.container}
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing}
                            onRefresh={onRefresh}
                            colors={["#F97316"]}
                            tintColor="#F97316"
                        />
                    }
                >
                    {/* FILTER */}
                    <View style={styles.filterCard}>
                        <View style={styles.filterHeader}>
                            <Text>🔎 </Text>
                            <Text style={styles.filterTitle}>
                                Filter History
                            </Text>
                        </View>

                        <ScrollView horizontal>
                            {[
                                "Weekly",
                                "Monthly",
                                "Quarterly",
                                "Yearly",
                            ].map((type) => {
                                const active =
                                    filterType === type;
                                return (
                                    <Pressable
                                        key={type}
                                        onPress={() =>
                                            handleFilterChange(
                                                type as FilterType
                                            )
                                        }
                                        style={[
                                            styles.filterBtn,
                                            active &&
                                            styles.filterBtnActive,
                                        ]}
                                    >
                                        <Text
                                            style={[
                                                styles.filterBtnText,
                                                active &&
                                                styles.filterBtnTextActive,
                                            ]}
                                        >
                                            {type}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                        </ScrollView>

                        <View style={styles.dateRow}>
                            <View style={{ flex: 1 }}>
                                <Text style={styles.dateLabel}>
                                    From Date
                                </Text>
                                <Pressable
                                    style={styles.dateBox}
                                    onPress={() =>
                                        openPicker("from")
                                    }
                                >
                                    <Text>📅 </Text>
                                    <Text style={styles.dateText}>
                                        {format(
                                            new Date(fromDate),
                                            "dd-MM-yyyy"
                                        )}
                                    </Text>
                                </Pressable>
                            </View>

                            <View style={{ flex: 1 }}>
                                <Text style={styles.dateLabel}>
                                    To Date
                                </Text>
                                <Pressable
                                    style={styles.dateBox}
                                    onPress={() =>
                                        openPicker("to")
                                    }
                                >
                                    <Text>📅 </Text>
                                    <Text style={styles.dateText}>
                                        {format(
                                            new Date(toDate),
                                            "dd-MM-yyyy"
                                        )}
                                    </Text>
                                </Pressable>
                            </View>
                        </View>
                    </View>

                    {/* LIST */}
                    {loading && <ActivityIndicator />}

                    {!loading &&
                        entries.map((item, index) => {
                            const dt = tryParseHistoryDate(item.date);
                            const minutes =
                                parseInt(
                                    item.data.duration?.split(
                                        ":"
                                    )[1] || "0",
                                    10
                                );

                            return (
                                <View
                                    key={index}
                                    style={styles.historyCard}
                                >
                                    <View
                                        style={styles.historyTop}
                                    >
                                        <Text style={styles.historyDate}>
                                            {dt ? format(dt, "EEE, dd MMM yyyy") : "-"}
                                        </Text>

                                        <View style={styles.actions}>
                                            {item.data.isEditable ? (
                                                <Pressable
                                                    onPress={() =>
                                                        openEditModal(item)
                                                    }
                                                    style={styles.iconBtn}
                                                    disabled={loading}
                                                >
                                                    <Text>✏️</Text>
                                                </Pressable>
                                            ) : (
                                                <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                                            )}

                                            <Pressable
                                                onPress={() =>
                                                    handleDelete(
                                                        item.data
                                                            .exerciseId
                                                    )
                                                }
                                                style={[styles.iconBtn, styles.iconBtnDanger]}
                                                disabled={loading}
                                            >
                                                <Text>🗑️</Text>
                                            </Pressable>
                                        </View>
                                    </View>

                                    <View
                                        style={styles.labelRow}
                                    >
                                        <Text
                                            style={
                                                styles.programLabel
                                            }
                                        >
                                            PROGRAM
                                        </Text>
                                        <Text
                                            style={
                                                styles.durationLabel
                                            }
                                        >
                                            DURATION
                                        </Text>
                                    </View>

                                    <View
                                        style={styles.valueRow}
                                    >
                                        <View
                                            style={{
                                                flexDirection: "row",
                                            }}
                                        >
                                            <Text>🏃 </Text>
                                            <Text
                                                style={
                                                    styles.programText
                                                }
                                            >
                                                {item.data
                                                    .otherType ||
                                                    item.data
                                                        .exerciseTypeName}
                                            </Text>
                                        </View>

                                        <Text
                                            style={
                                                styles.durationText
                                            }
                                        >
                                            {minutes}
                                        </Text>
                                    </View>
                                </View>
                            );
                        })}
                </ScrollView>

                {/* ================= EDIT MODAL ================= */}
                <Modal
                    visible={editVisible}
                    transparent
                    animationType="fade"
                    onRequestClose={() => {
                        if (!loading) setEditVisible(false);
                    }}
                >
                    <View style={styles.modalOverlayExact}>
                        <View style={styles.modalCardExact}>

                            {/* HEADER */}
                            <View style={styles.modalHeaderExact}>
                                <Text style={styles.modalTitleExact}>Edit Exercise</Text>

                                <Pressable onPress={() => setEditVisible(false)} disabled={loading}>
                                    <Text style={styles.closeIconExact}>✕</Text>
                                </Pressable>
                            </View>

                            {/* DATE */}
                            <Text style={styles.modalDateExact}>
                                {(() => {
                                    const modalDateObj = tryParseHistoryDate(editData?.date);
                                    return (
                                        <Text style={styles.modalDateExact}>
                                            Date: {modalDateObj ? format(modalDateObj, "dd MMMM yyyy") : "-"}
                                        </Text>
                                    );
                                })()}
                            </Text>

                            {/* INPUTS */}
                            <View style={{ gap: 16, marginTop: 18 }}>

                                <View>
                                    <Text style={styles.inputLabelExact}>Time (Minutes)</Text>
                                    <Input
                                        keyboardType="numeric"
                                        value={editMinutes}
                                        onChangeText={(text) =>
                                            setEditMinutes(text.replace(/[^0-9]/g, ""))
                                        }
                                    />
                                    {editMinutesError ? (
                                        <Text style={styles.validationText}>{editMinutesError}</Text>
                                    ) : null}
                                </View>

                                {/* Exercise Program Dropdown */}
                                {/* <View>
                                    <Text style={styles.inputLabelExact}>Exercise Program</Text>

                                    <Pressable
                                        style={[styles.selectBoxExact, styles.disabledSelect]}
                                        disabled
                                    >
                                        <Text style={{ fontWeight: "700" }}>
                                            {types.find(
                                                (t) => t.enumerationId === editExerciseTypeId
                                            )?.value || "Select Exercise Type"}
                                        </Text>
                                    </Pressable>
                                </View> */}

                                {types.find(
                                    (t) => t.enumerationId === editExerciseTypeId
                                )?.value === "Other" && (
                                        <View>
                                            <Text style={styles.inputLabelExact}>
                                                Custom Exercise Type
                                            </Text>
                                            <Input
                                                value={editOtherType}
                                                onChangeText={setEditOtherType}
                                            />
                                        </View>
                                    )}

                            </View>

                            {/* BUTTONS */}
                            <View style={styles.modalButtonRowExact}>
                                <Pressable
                                    style={styles.cancelBtnExact}
                                    onPress={() => setEditVisible(false)}
                                    disabled={loading}
                                >
                                    <Text style={styles.cancelTextExact}>Cancel</Text>
                                </Pressable>

                                <Pressable
                                    style={[styles.updateBtnExact, loading && styles.buttonDisabled]}
                                    onPress={handleUpdate}
                                    disabled={loading || !!editMinutesError}
                                >
                                    <Text style={styles.updateTextExact}>{loading ? "Updating..." : "Update"}</Text>
                                </Pressable>
                            </View>

                        </View>
                    </View>
                </Modal>

                <ConfirmDialog
                    visible={pendingDeleteId != null}
                    title="Delete Exercise"
                    message="Are you sure you want to delete this record?"
                    confirmText="Delete"
                    destructive
                    loading={loading}
                    onCancel={() => setPendingDeleteId(null)}
                    onConfirm={async () => {
                        if (pendingDeleteId == null) return;
                        try {
                            setLoading(true);
                            const res = await ExerciseService.delete(pendingDeleteId.toString());

                            if (res?.status === 200) {
                                Flash.success("Success!", "Exercise Deleted Successfully");
                                setPendingDeleteId(null);
                                loadHistory();
                            } else {
                                Flash.error("Delete failed");
                            }
                        } finally {
                            setLoading(false);
                        }
                    }}
                />

            </View>
        </DivineBackground>
    );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
    container: { padding: 14 },

    filterCard: {
        backgroundColor: "white",
        borderRadius: 20,
        padding: 18,
        marginBottom: 18,
        elevation: 6,
    },

    filterHeader: {
        flexDirection: "row",
        marginBottom: 10,
    },

    filterTitle: {
        fontWeight: "900",
    },

    filterBtn: {
        paddingHorizontal: 18,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: "#e0f2fe",
        marginRight: 10,
    },

    filterBtnActive: {
        backgroundColor: "#5581d8",
    },

    disabledSelect: {
        backgroundColor: "#f3f4f6",
        opacity: 0.7,
    },

    filterBtnText: {
        fontWeight: "800",
    },

    filterBtnTextActive: {
        color: "white",
    },
    validationText: {
        marginTop: 6,
        fontSize: 11,
        fontWeight: "800",
        color: "#DC2626",
    },

    dateRow: {
        flexDirection: "row",
        gap: 12,
        marginTop: 14,
    },

    dateLabel: {
        fontWeight: "700",
    },

    dateBox: {
        flexDirection: "row",
        backgroundColor: "#f3f4f6",
        padding: 12,
        borderRadius: 12,
    },

    historyCard: {
        backgroundColor: "white",
        borderRadius: 18,
        padding: 16,
        marginBottom: 14,
    },

    historyTop: {
        flexDirection: "row",
        justifyContent: "space-between",
    },

    historyDate: {
        fontWeight: "800",
        color: "#6a94e8",
    },

    labelRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 8,
    },

    programLabel: {
        fontSize: 11,
        color: "#6b7280",
    },

    durationLabel: {
        fontSize: 11,
        color: "#6b7280",
    },

    valueRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 4,
    },

    programText: {
        fontSize: 15,
        fontWeight: "800",
    },

    durationText: {
        fontSize: 18,
        fontWeight: "900",
        color: "#f46734",
    },

    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.4)",
        justifyContent: "center",
        padding: 20,
    },

    modalCard: {
        backgroundColor: "white",
        borderRadius: 20,
        padding: 20,
    },

    modalTitle: {
        fontWeight: "900",
        fontSize: 18,
        marginBottom: 12,
    },

    selectBox: {
        borderWidth: 1,
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
    },

    dateText: {
        fontSize: 13,
        fontWeight: "700",
        color: "#374151",
    },
    actions: {
        flexDirection: "row",
        gap: 10,
    },
    iconBtn: {
        minWidth: 45,
        height: 34,
        paddingHorizontal: 10,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(59,130,246,0.10)",
    },
    iconBtnDanger: {
        backgroundColor: "rgba(220,38,38,0.10)",
    },
    iconBtnPlaceholder: {
        opacity: 0,
    },

    /* ===== EXACT MODAL STYLE (Diet Screen Style) ===== */

    modalOverlayExact: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.45)",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
    },

    modalCardExact: {
        width: "100%",
        backgroundColor: "#ffffff",
        borderRadius: 20,
        padding: 24,
        shadowColor: "#000",
        shadowOpacity: 0.25,
        shadowRadius: 25,
        shadowOffset: { width: 0, height: 15 },
        elevation: 10,
    },

    modalHeaderExact: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    modalTitleExact: {
        fontSize: 18,
        fontWeight: "800",
        color: "#111827",
    },

    closeIconExact: {
        fontSize: 18,
        color: "#6B7280",
        fontWeight: "600",
    },

    modalDateExact: {
        marginTop: 8,
        fontSize: 13,
        color: "#6B7280",
    },

    inputLabelExact: {
        fontSize: 13,
        fontWeight: "700",
        marginBottom: 6,
        color: "#374151",
    },

    selectBoxExact: {
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.12)",
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 14,
        backgroundColor: "#F9FAFB",
    },

    modalButtonRowExact: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 26,
        gap: 14,
    },

    cancelBtnExact: {
        flex: 1,
        height: 48,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: "#F97316",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#ffffff",
    },

    cancelTextExact: {
        color: "#F97316",
        fontWeight: "700",
        fontSize: 15,
    },

    updateBtnExact: {
        flex: 1,
        height: 48,
        borderRadius: 12,
        backgroundColor: "#6a94e8",
        justifyContent: "center",
        alignItems: "center",
        shadowColor: "#6a94e8",
        shadowOpacity: 0.4,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 6,
    },

    updateTextExact: {
        color: "#ffffff",
        fontWeight: "800",
        fontSize: 15,
    },
    buttonDisabled: {
        opacity: 0.6,
    },
});
